import React from "react";
import {IOrderDetail} from "../models/IOrderDetail";
import {Stack} from "@mui/material";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import {styled} from "@mui/material/styles";
import TableCell, {tableCellClasses} from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import {ProcessButton} from "../../../components/common/input/ProcessButton";
import {Download} from "@mui/icons-material";
import {graphQLServices} from "../../../services/GraphQLServices";
import {GET_ORDERS_WITH_INCENTIVE} from "../GraphQLQueries";
import {OrderQueries} from "../models/OrderQueries";
import {ApolloQueryResult} from "@apollo/client/core/types";
import {download} from "../../../components/common/DownloadButton";

export interface IncentiveDataProps {
	readonly order: IOrderDetail;
}

export function IncentiveData(props: IncentiveDataProps) {
	const rows = [
		createData("User desires incentive", (props.order.userDesiresIncentive ?? false) + ""),
		createData("Name", props.order.mainApplicant?.callName),
		createData("Email", props.order.mainApplicant?.email),
	];
	
	return <Stack>
		<TableContainer component={Paper}>
			<Table sx={{minWidth: 650}} size="small" aria-label="Data">
				<TableBody>
					{rows.map((row) => (
						<StyledTableRow
							key={row.name}
						>
							<StyledTableCell component="th" scope="row">
								{row.name}
							</StyledTableCell>
							<StyledTableCell align="right">{row.value}</StyledTableCell>
						</StyledTableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	</Stack>;
}

export interface IncentiveDataExportProps {
	readonly whereClause: () => any;
}

export function IncentiveDataExport(props: IncentiveDataExportProps) {
	return <ProcessButton
		variant={"outlined"}
		startIcon={<Download/>}
		defaultText={"Download incentive orders"}
		inProgressText={"Downloading incentive orders"}
		completedText={"Downloaded incentive orders"}
		onClick={() => downloadAllOrdersWithIncentive(props.whereClause)}
	/>;
}

async function downloadAllOrdersWithIncentive(whereClause: () => any) {
	const client = graphQLServices.orderClient;
	
	const nodes: IOrderDetail[] = [];
	
	const where = whereClause();
	
	let cursor: string | null = null;
	let hasNextPage = false;
	do {
		const response: ApolloQueryResult<OrderQueries> = await client.query<OrderQueries>({
			query: GET_ORDERS_WITH_INCENTIVE,
			variables: {
				where: where,
				after: cursor
			}
		});
		
		nodes.push(...response.data.orderDetails.nodes);
		
		hasNextPage = response.data.orderDetails.pageInfo.hasNextPage ?? false;
		cursor = response.data.orderDetails.pageInfo.endCursor;
	} while (hasNextPage);
	
	const header = [
		"id",
		"creationDateTime",
		"status",
		"underwritingResult",
		"userDesiresIncentive",
		"apiClientName",
		"callName",
		"email",
		"companyNameDescription",
	];
	
	const rows = nodes
		.map(it => {
			return [
				it.id,
				it.creationDateTime ?? "",
				(it.status ?? "") + "",
				(it.underwritingResult ?? "") + "",
				(it.userDesiresIncentive ?? "") + "",
				it.apiClientName ?? "",
				it.mainApplicant?.callName ?? "",
				it.mainApplicant?.email ?? "",
				it.package?.companyNameDescription ?? "",
			];
		});
	
	const data = [header].concat(rows);
	const csv = toCsv(data);
	
	download("export-orders-incentive.csv", csv, {
		data: "text/csv"
	});
}

function toCsv(data: string[][]): string {
	return data
		.map(row => row
			.map(cell =>
				cell.indexOf(",") >= 0 || cell.indexOf("\n") >= 0 || cell.indexOf("\"") >= 0
					? "\"" + cell.replaceAll("\"", "\"\"") + "\""
					: cell)
			.join(","))
		.join("\n");
}

function createData(
	name: string,
	value: string | null | undefined,
) {
	if (!value)
		value = "";
	return {name, value};
}

const StyledTableCell = styled(TableCell)(({theme}) => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: theme.palette.common.black,
		color: theme.palette.common.white,
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 14,
	},
}));

const StyledTableRow = styled(TableRow)(({theme}) => ({
	"&:nth-of-type(odd)": {
		backgroundColor: theme.palette.action.hover,
	},
	// hide last border
	"&:last-child td, &:last-child th": {
		border: 0,
	},
}));
